/**
 * Site-specific legal texts (legal notice / Impressum, privacy policy, cookie
 * policy) for fi, en and de, written in October 2026 from an audit of what this
 * website actually does:
 *  - no cookies, no analytics, no advertising or tracking scripts, no
 *    third-party resources on page load (fonts are self-hosted);
 *  - Netlify hosts the site and keeps server logs;
 *  - form submissions go through EmailJS (Formspree on the Polish pages);
 *  - demo booking links out to Calendly (no embed);
 *  - browser storage only for language choice, dismissed notices and form
 *    progress (strictly necessary / user-requested).
 * If the site adds analytics, embeds or new forms, update these texts first.
 *
 * The Finnish text is the master. The English and German versions follow the
 * same structure — TARKISTETTAVA (to be reviewed): translations, have them
 * checked (German by a German-law reviewer because of Abmahnung risk).
 *
 * Open items are marked "TODO" in the visible text on purpose, so a missing fact
 * can never be published unnoticed. Do not deploy while any TODO remains.
 */

/**
 * Publish switch. While false, the legal notice pages are not generated (no
 * footer link, not in the sitemap) and the privacy/cookie pages keep their
 * previous text. Turn on once every visible TODO below has been filled in.
 */
export const LEGAL_TEXTS_READY = true;

export type LegalLang = 'fi' | 'en' | 'de';

export interface LegalSection {
  h2: string;
  paragraphs?: string[];
  list?: string[];
  /** Rendered after the list. */
  after?: string[];
}

export interface LegalDoc {
  title: string;
  updatedLabel: string;
  updated: string; // ISO date of the last content change
  intro?: string[];
  sections: LegalSection[];
}

export const COMPANY = {
  name: 'ClimaPri Oy',
  street: 'Koskelontie 22',
  postal: '02920 Espoo',
  businessId: '2823998-8',
  vatId: 'FI28239988',
  phone: '+358 20 7032 970',
  /** Export Director's number; the non-Finnish pages do not show the 020 switchboard. */
  phoneIntl: '+358 50 435 7842',
  email: 'info@climapri.com',
  /** Representative as named elsewhere on the site (contact and careers pages). */
  representative: 'Kimmo Pelkonen',
};

const UPDATED = '2026-10-23';

/* ------------------------------------------------------------------ */
/* Legal notice / Julkaisutiedot / Impressum                           */
/* ------------------------------------------------------------------ */

export const LEGAL_NOTICE: Record<LegalLang, LegalDoc> = {
  fi: {
    title: 'Julkaisutiedot',
    updatedLabel: 'Päivitetty',
    updated: UPDATED,
    sections: [
      { h2: 'Palveluntarjoaja', paragraphs: [`${COMPANY.name}`, `${COMPANY.street}`, `${COMPANY.postal}, Suomi`] },
      { h2: 'Yhteystiedot', paragraphs: [`Puhelin: ${COMPANY.phone}`, `Sähköposti: ${COMPANY.email}`] },
      {
        h2: 'Kaupparekisteri ja tunnisteet',
        paragraphs: [
          `Y-tunnus: ${COMPANY.businessId}`,
          'Rekisteri: kaupparekisteri, Patentti- ja rekisterihallitus',
          `ALV-tunniste: ${COMPANY.vatId}`,
        ],
      },
      { h2: 'Edustaja', paragraphs: [`${COMPANY.representative}, perustaja, konsernin toimitusjohtaja`] },
    ],
  },
  // TARKISTETTAVA: English translation of the Finnish legal notice.
  en: {
    title: 'Legal notice',
    updatedLabel: 'Updated',
    updated: UPDATED,
    sections: [
      { h2: 'Service provider', paragraphs: [`${COMPANY.name}`, `${COMPANY.street}`, `${COMPANY.postal}, Finland`] },
      { h2: 'Contact', paragraphs: [`Phone: ${COMPANY.phoneIntl}`, `Email: ${COMPANY.email}`] },
      {
        h2: 'Company register and identifiers',
        paragraphs: [
          `Business ID: ${COMPANY.businessId}`,
          'Register: Trade Register of Finland, Finnish Patent and Registration Office',
          `VAT ID: ${COMPANY.vatId}`,
        ],
      },
      { h2: 'Representative', paragraphs: [`${COMPANY.representative}, Founder & Group CEO`] },
    ],
  },
  // TARKISTETTAVA: German translation; Impressum per § 5 DDG — have it reviewed.
  de: {
    title: 'Impressum',
    updatedLabel: 'Stand',
    updated: UPDATED,
    intro: ['Angaben gemäß § 5 Digitale-Dienste-Gesetz (DDG)'],
    sections: [
      {
        h2: 'Anbieter',
        paragraphs: [`${COMPANY.name} (Osakeyhtiö, finnische Kapitalgesellschaft)`, `${COMPANY.street}`, `${COMPANY.postal}, Finnland`],
      },
      { h2: 'Kontakt', paragraphs: [`Telefon: ${COMPANY.phoneIntl}`, `E-Mail: ${COMPANY.email}`] },
      {
        h2: 'Registereintrag und Umsatzsteuer',
        paragraphs: [
          'Registergericht/Register: Finnisches Handelsregister (Patentti- ja rekisterihallitus)',
          `Registernummer (Y-tunnus): ${COMPANY.businessId}`,
          `Umsatzsteuer-Identifikationsnummer: ${COMPANY.vatId}`,
        ],
      },
      { h2: 'Vertretungsberechtigt', paragraphs: [`${COMPANY.representative}, Gründer & Group-CEO`] },
    ],
  },
};

/* ------------------------------------------------------------------ */
/* Privacy policy / Tietosuojaseloste / Datenschutzerklärung            */
/* ------------------------------------------------------------------ */

export const PRIVACY: Record<LegalLang, LegalDoc> = {
  fi: {
    title: 'Tietosuojaseloste',
    updatedLabel: 'Päivitetty',
    updated: UPDATED,
    intro: [
      'Tämä seloste kertoo, mitä henkilötietoja ClimaPrin verkkosivusto (climapri.fi, climapri.com, climapri.se, climapri.dk ja climapri.pl) käsittelee. Se koskee vain tätä verkkosivustoa. ClimaPri Cloud -palvelulla on oma selosteensa.',
    ],
    sections: [
      {
        h2: '1. Rekisterinpitäjä ja yhteystiedot',
        paragraphs: [
          `${COMPANY.name} (Y-tunnus ${COMPANY.businessId})`,
          `${COMPANY.street}, ${COMPANY.postal}, Suomi`,
          `Sähköposti: ${COMPANY.email}, puhelin: ${COMPANY.phone}`,
          'Tietosuoja-asioissa voit ottaa yhteyttä yllä olevaan sähköpostiosoitteeseen.',
        ],
      },
      {
        h2: '2. Mitä tietoja käsittelemme',
        list: [
          'Palvelinlokit: kun käytät sivustoa, sivuston ylläpitopalvelu kirjaa teknisiä tietoja, kuten IP-osoitteen, käyntiajankohdan, pyydetyn sivun, selaimen tunnisteen ja viittaavan sivun. Ylläpitopalvelu säilyttää IP-osoitteita sisältävät käyttölokit Netlifyn ilmoituksen mukaan alle 30 päivää.',
          'Yhteydenotot ja lomakkeet: kun lähetät yhteydenotto-, säästöarvio- tai kyselylomakkeen, käsittelemme antamasi tiedot, kuten nimen, sähköpostiosoitteen, puhelinnumeron, yrityksen tai taloyhtiön nimen, viestin ja kohteen tiedot.',
          'Selaimen paikallinen tallennus: sivusto tallentaa selaimeesi kielivalintasi, tiedon suljetuista ilmoituksista ja lomakkeen keskeneräisen täytön. Lomakkeen tiedot poistuvat, kun suljet selainvälilehden.',
        ],
        after: ['Sivusto ei käytä evästeitä, analytiikkaa eikä mainos- tai seurantatyökaluja.'],
      },
      {
        h2: '3. Käyttötarkoitus ja oikeusperuste',
        list: [
          'Palvelinlokit: sivuston toiminnan ja tietoturvan varmistaminen. Oikeusperuste on oikeutettu etumme (EU:n yleisen tietosuoja-asetuksen 6 artiklan 1 kohdan f alakohta).',
          'Lomakkeet: yhteydenottoosi vastaaminen ja pyytämäsi tarjouksen tai säästöarvion laatiminen. Oikeusperuste on sopimusta edeltävien toimien toteuttaminen pyynnöstäsi (6 artiklan 1 kohdan b alakohta) ja yritysten välisessä yhteydenpidossa oikeutettu etumme (6 artiklan 1 kohdan f alakohta).',
          'Selaimen paikallinen tallennus: pyytämäsi toiminnallisuuden toteuttaminen. Tallennus on välttämätöntä tätä varten, eikä se edellytä suostumusta.',
        ],
      },
      {
        h2: '4. Säilytysajat',
        list: [
          'Palvelinlokit: alle 30 päivää (ylläpitopalvelu Netlify).',
          'Lomakkeilla lähetetyt tiedot: niin kauan kuin asian hoitaminen ja mahdollinen asiakassuhde edellyttävät.',
          'Selaimen paikallinen tallennus: kunnes tyhjennät selaimen tallennetut tiedot. Lomakkeen keskeneräiset tiedot poistuvat, kun välilehti suljetaan.',
        ],
      },
      {
        h2: '5. Tietojen vastaanottajat ja siirrot EU:n ulkopuolelle',
        paragraphs: ['Emme myy tai luovuta tietojasi markkinointiin. Käytämme seuraavia palveluntarjoajia, jotka käsittelevät tietoja lukuumme:'],
        list: [
          'Netlify, Inc. (Yhdysvallat): sivuston ylläpito ja palvelinlokit.',
          'EmailJS Pte. Ltd. (Singapore, palvelimet Yhdysvalloissa): lomakkeiden välittäminen sähköpostiimme.',
          'Formspree, Inc. (Yhdysvallat): Puolan kieliversion lomakkeiden välittäminen.',
          'Microsoft 365 (Microsoft Ireland Operations Limited): sähköpostipalvelumme, johon lomakkeiden viestit saapuvat.',
        ],
        after: [
          'Siirrot EU:n ja ETA-alueen ulkopuolelle perustuvat seuraaviin suojatoimiin: Netlify on sitoutunut EU:n ja Yhdysvaltain väliseen tietosuojakehykseen (Data Privacy Framework), ja sen tietojenkäsittelysopimus sisältää Euroopan komission vakiosopimuslausekkeet. EmailJS ja Formspree käyttävät vakiosopimuslausekkeita. Microsoft 365 säilyttää tiedot EU:ssa (EU Data Boundary); mahdolliset siirrot EU:n ulkopuolelle perustuvat tietosuojakehykseen ja vakiosopimuslausekkeisiin.',
        ],
      },
      {
        h2: '6. Linkit ulkoisiin palveluihin',
        paragraphs: [
          'Demon ajanvaraus avautuu Calendly-palveluun, ja sosiaalisen median linkit vievät Facebookiin, LinkedIniin ja Instagramiin. Niiden tietojenkäsittelyyn sovelletaan kyseisten palvelujen omia tietosuojakäytäntöjä. Sivustomme ei lataa näiden palvelujen skriptejä.',
        ],
      },
      {
        h2: '7. Oikeutesi',
        paragraphs: ['Sinulla on oikeus:'],
        list: [
          'saada pääsy tietoihisi',
          'vaatia virheellisten tietojen oikaisemista',
          'vaatia tietojesi poistamista',
          'vaatia käsittelyn rajoittamista',
          'vastustaa oikeutettuun etuun perustuvaa käsittelyä',
          'siirtää antamasi tiedot järjestelmästä toiseen.',
        ],
        after: [`Voit käyttää oikeuksiasi ottamalla yhteyttä osoitteeseen ${COMPANY.email}.`],
      },
      {
        h2: '8. Valitusoikeus',
        paragraphs: [
          'Jos katsot, että tietojesi käsittely rikkoo tietosuojalainsäädäntöä, voit tehdä valituksen tietosuojavaltuutetun toimistolle (tietosuoja.fi) tai asuin- tai työskentelymaasi valvontaviranomaiselle.',
        ],
      },
      {
        h2: '9. Muutokset',
        paragraphs: ['Päivitämme tätä selostetta, jos sivuston tietojenkäsittely muuttuu. Päivityspäivä näkyy selosteen alussa.'],
      },
    ],
  },
  // TARKISTETTAVA: English translation of the Finnish privacy policy.
  en: {
    title: 'Privacy policy',
    updatedLabel: 'Updated',
    updated: UPDATED,
    intro: [
      'This policy explains what personal data the ClimaPri website (climapri.fi, climapri.com, climapri.se, climapri.dk and climapri.pl) processes. It covers this website only. The ClimaPri Cloud service has its own policy.',
    ],
    sections: [
      {
        h2: '1. Controller and contact details',
        paragraphs: [
          `${COMPANY.name} (Business ID ${COMPANY.businessId})`,
          `${COMPANY.street}, ${COMPANY.postal}, Finland`,
          `Email: ${COMPANY.email}, phone: ${COMPANY.phoneIntl}`,
          'For data protection matters, contact us at the email address above.',
        ],
      },
      {
        h2: '2. What data we process',
        list: [
          'Server logs: when you use the site, our hosting provider records technical data such as your IP address, time of visit, requested page, browser identifier and referring page. According to Netlify, access logs containing IP addresses are kept for less than 30 days.',
          'Contact and other forms: when you submit a contact, savings estimate or questionnaire form, we process the data you provide, such as your name, email address, phone number, company or housing company, message and property details.',
          'Local browser storage: the site stores your language choice, dismissed notices and an unfinished form in your browser. Form data is deleted when you close the browser tab.',
        ],
        after: ['The site does not use cookies, analytics, or advertising or tracking tools.'],
      },
      {
        h2: '3. Purposes and legal bases',
        list: [
          'Server logs: operating and securing the website. Legal basis: our legitimate interest (Article 6(1)(f) GDPR).',
          'Forms: answering your enquiry and preparing the quote or savings estimate you request. Legal basis: steps taken at your request prior to entering into a contract (Article 6(1)(b) GDPR) and, in business-to-business contact, our legitimate interest (Article 6(1)(f) GDPR).',
          'Local browser storage: providing the functionality you request. The storage is strictly necessary for this and does not require consent.',
        ],
      },
      {
        h2: '4. Retention periods',
        list: [
          'Server logs: less than 30 days (hosting provider Netlify).',
          'Data submitted through forms: as long as handling your matter and any customer relationship requires.',
          'Local browser storage: until you clear your browser data. Unfinished form data is deleted when the tab is closed.',
        ],
      },
      {
        h2: '5. Recipients and transfers outside the EU',
        paragraphs: ['We do not sell your data or disclose it for marketing. The following service providers process data on our behalf:'],
        list: [
          'Netlify, Inc. (United States): website hosting and server logs.',
          'EmailJS Pte. Ltd. (Singapore, servers in the United States): forwarding form submissions to our email.',
          'Formspree, Inc. (United States): forwarding form submissions on the Polish pages.',
          'Microsoft 365 (Microsoft Ireland Operations Limited): our email service, where form messages arrive.',
        ],
        after: [
          'Transfers outside the EU/EEA rely on the following safeguards: Netlify is certified under the EU-U.S. Data Privacy Framework, and its data processing agreement includes the European Commission’s standard contractual clauses. EmailJS and Formspree use standard contractual clauses. Microsoft 365 stores data in the EU (EU Data Boundary); any transfers outside the EU rely on the Data Privacy Framework and standard contractual clauses.',
        ],
      },
      {
        h2: '6. Links to external services',
        paragraphs: [
          'Demo booking opens the Calendly service, and the social media links lead to Facebook, LinkedIn and Instagram. Their own privacy policies apply to their processing. Our website does not load scripts from these services.',
        ],
      },
      {
        h2: '7. Your rights',
        paragraphs: ['You have the right to:'],
        list: [
          'access your data',
          'have inaccurate data rectified',
          'have your data erased',
          'restrict processing',
          'object to processing based on legitimate interest',
          'receive the data you provided in a portable format.',
        ],
        after: [`To exercise your rights, contact ${COMPANY.email}.`],
      },
      {
        h2: '8. Right to lodge a complaint',
        paragraphs: [
          'If you believe the processing of your data infringes data protection law, you can lodge a complaint with the Office of the Data Protection Ombudsman in Finland (tietosuoja.fi) or with the supervisory authority of your country of residence or work.',
        ],
      },
      {
        h2: '9. Changes',
        paragraphs: ['We update this policy if the processing on the website changes. The date of the latest update is shown at the top.'],
      },
    ],
  },
  // TARKISTETTAVA: German translation of the Finnish privacy policy — have it reviewed.
  de: {
    title: 'Datenschutzerklärung',
    updatedLabel: 'Stand',
    updated: UPDATED,
    intro: [
      'Diese Erklärung beschreibt, welche personenbezogenen Daten die Website von ClimaPri (climapri.fi, climapri.com, climapri.se, climapri.dk und climapri.pl) verarbeitet. Sie gilt nur für diese Website. Für den Dienst ClimaPri Cloud gilt eine eigene Datenschutzerklärung.',
    ],
    sections: [
      {
        h2: '1. Verantwortlicher und Kontakt',
        paragraphs: [
          `${COMPANY.name} (Unternehmenskennung ${COMPANY.businessId})`,
          `${COMPANY.street}, ${COMPANY.postal}, Finnland`,
          `E-Mail: ${COMPANY.email}, Telefon: ${COMPANY.phoneIntl}`,
          'Bei Fragen zum Datenschutz erreichen Sie uns unter der oben genannten E-Mail-Adresse.',
        ],
      },
      {
        h2: '2. Welche Daten wir verarbeiten',
        list: [
          'Server-Logfiles: Wenn Sie die Website nutzen, erfasst unser Hosting-Anbieter technische Daten wie IP-Adresse, Zeitpunkt des Zugriffs, aufgerufene Seite, Browserkennung und verweisende Seite. Nach Angaben von Netlify werden Zugriffsprotokolle mit IP-Adressen weniger als 30 Tage gespeichert.',
          'Kontakt- und sonstige Formulare: Wenn Sie ein Kontakt-, Einsparungs- oder Fragebogenformular absenden, verarbeiten wir Ihre Angaben wie Name, E-Mail-Adresse, Telefonnummer, Unternehmen bzw. Wohnungsgesellschaft, Nachricht und Angaben zum Objekt.',
          'Lokale Speicherung im Browser: Die Website speichert Ihre Sprachwahl, geschlossene Hinweise und ein nicht abgeschlossenes Formular in Ihrem Browser. Formulardaten werden gelöscht, wenn Sie den Browser-Tab schließen.',
        ],
        after: ['Die Website verwendet keine Cookies, keine Analyse und keine Werbe- oder Tracking-Werkzeuge.'],
      },
      {
        h2: '3. Zwecke und Rechtsgrundlagen',
        list: [
          'Server-Logfiles: Betrieb und Sicherheit der Website. Rechtsgrundlage ist unser berechtigtes Interesse (Art. 6 Abs. 1 lit. f DSGVO).',
          'Formulare: Beantwortung Ihrer Anfrage und Erstellung des gewünschten Angebots oder der Einsparungsschätzung. Rechtsgrundlage ist die Durchführung vorvertraglicher Maßnahmen auf Ihre Anfrage (Art. 6 Abs. 1 lit. b DSGVO) sowie im geschäftlichen Kontakt unser berechtigtes Interesse (Art. 6 Abs. 1 lit. f DSGVO).',
          'Lokale Speicherung im Browser: Bereitstellung der von Ihnen gewünschten Funktion. Die Speicherung ist dafür unbedingt erforderlich und bedarf keiner Einwilligung (§ 25 Abs. 2 Nr. 2 TDDDG).',
        ],
      },
      {
        h2: '4. Speicherdauer',
        list: [
          'Server-Logfiles: weniger als 30 Tage (Hosting-Anbieter Netlify).',
          'Über Formulare übermittelte Daten: so lange, wie die Bearbeitung Ihres Anliegens und eine etwaige Kundenbeziehung es erfordern.',
          'Lokale Speicherung im Browser: bis Sie die Browserdaten löschen. Daten eines nicht abgeschlossenen Formulars werden beim Schließen des Tabs gelöscht.',
        ],
      },
      {
        h2: '5. Empfänger und Übermittlung in Drittländer',
        paragraphs: ['Wir verkaufen Ihre Daten nicht und geben sie nicht für Werbezwecke weiter. Folgende Dienstleister verarbeiten Daten in unserem Auftrag:'],
        list: [
          'Netlify, Inc. (USA): Hosting der Website und Server-Logfiles.',
          'EmailJS Pte. Ltd. (Singapur, Server in den USA): Weiterleitung von Formularanfragen an unser E-Mail-Postfach.',
          'Formspree, Inc. (USA): Weiterleitung von Formularanfragen auf den polnischen Seiten.',
          'Microsoft 365 (Microsoft Ireland Operations Limited): unser E-Mail-Dienst, in dem Formularanfragen eingehen.',
        ],
        after: [
          'Übermittlungen außerhalb der EU bzw. des EWR stützen sich auf folgende Garantien: Netlify ist unter dem EU-US Data Privacy Framework zertifiziert, und sein Auftragsverarbeitungsvertrag enthält die Standardvertragsklauseln der Europäischen Kommission. EmailJS und Formspree verwenden Standardvertragsklauseln. Microsoft 365 speichert die Daten in der EU (EU Data Boundary); etwaige Übermittlungen außerhalb der EU stützen sich auf das Data Privacy Framework und Standardvertragsklauseln.',
        ],
      },
      {
        h2: '6. Links zu externen Diensten',
        paragraphs: [
          'Die Terminbuchung für eine Demo öffnet den Dienst Calendly, die Social-Media-Links führen zu Facebook, LinkedIn und Instagram. Für deren Datenverarbeitung gelten die Datenschutzbestimmungen dieser Dienste. Unsere Website lädt keine Skripte dieser Dienste.',
        ],
      },
      {
        h2: '7. Ihre Rechte',
        paragraphs: ['Sie haben das Recht auf:'],
        list: [
          'Auskunft über Ihre Daten',
          'Berichtigung unrichtiger Daten',
          'Löschung Ihrer Daten',
          'Einschränkung der Verarbeitung',
          'Widerspruch gegen eine auf berechtigtem Interesse beruhende Verarbeitung',
          'Datenübertragbarkeit der von Ihnen bereitgestellten Daten.',
        ],
        after: [`Zur Ausübung Ihrer Rechte wenden Sie sich an ${COMPANY.email}.`],
      },
      {
        h2: '8. Beschwerderecht',
        paragraphs: [
          'Wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer Daten gegen Datenschutzrecht verstößt, können Sie sich bei der finnischen Datenschutzbehörde (Office of the Data Protection Ombudsman, tietosuoja.fi) oder bei der Aufsichtsbehörde Ihres Aufenthalts- oder Arbeitsorts beschweren.',
        ],
      },
      {
        h2: '9. Änderungen',
        paragraphs: ['Wir aktualisieren diese Erklärung, wenn sich die Datenverarbeitung auf der Website ändert. Das Datum der letzten Aktualisierung steht oben.'],
      },
    ],
  },
};

/* ------------------------------------------------------------------ */
/* Cookie policy / Evästekäytäntö / Cookie-Richtlinie                  */
/* ------------------------------------------------------------------ */

export const COOKIES: Record<LegalLang, LegalDoc> = {
  fi: {
    title: 'Evästekäytäntö',
    updatedLabel: 'Päivitetty',
    updated: UPDATED,
    sections: [
      {
        h2: 'Sivusto ei käytä evästeitä',
        paragraphs: [
          'Tämä verkkosivusto ei aseta evästeitä eikä käytä analytiikkaa tai mainos- ja seurantatyökaluja. Sivu ei myöskään lataa kolmansien osapuolten sisältöä, kuten upotettuja videoita, karttoja tai ulkoisia fontteja. Siksi sivustolla ei ole evästebanneria.',
        ],
      },
      {
        h2: 'Selaimen paikallinen tallennus',
        paragraphs: ['Sivusto tallentaa selaimeesi vain pyytämäsi toiminnallisuuden edellyttämät tiedot:'],
        list: [
          'Kielivalinta: jotta sivusto näkyy valitsemallasi kielellä (säilyy, kunnes tyhjennät selaimen tiedot).',
          'Suljetut ilmoitukset: jotta sulkemasi ilmoitus ei näy uudelleen.',
          'Lomakkeen keskeneräinen täyttö: jotta säästöarviolomakkeen tiedot eivät katoa kesken täytön (poistuu, kun suljet välilehden).',
        ],
        after: ['Nämä tiedot pysyvät selaimessasi eivätkä siirry meille, eikä niitä käytetä seurantaan.'],
      },
      {
        h2: 'Ulkoiset palvelut',
        paragraphs: [
          'Demon ajanvaraus avautuu Calendly-palvelun omalle sivulle, ja sosiaalisen median linkit vievät kyseisiin palveluihin. Näillä palveluilla on omat evästekäytäntönsä. Lisätietoja tietojenkäsittelystä on tietosuojaselosteessa.',
        ],
      },
    ],
  },
  // TARKISTETTAVA: English translation of the Finnish cookie policy.
  en: {
    title: 'Cookie policy',
    updatedLabel: 'Updated',
    updated: UPDATED,
    sections: [
      {
        h2: 'This website does not use cookies',
        paragraphs: [
          'This website sets no cookies and uses no analytics, advertising or tracking tools. It does not load third-party content such as embedded videos, maps or external fonts either. That is why there is no cookie banner.',
        ],
      },
      {
        h2: 'Local browser storage',
        paragraphs: ['The site only stores what the functionality you request needs:'],
        list: [
          'Language choice: so the site appears in the language you chose (kept until you clear your browser data).',
          'Dismissed notices: so a notice you closed does not reappear.',
          'Unfinished form: so the savings estimate form keeps your answers while you fill it in (deleted when you close the tab).',
        ],
        after: ['This data stays in your browser and is not used for tracking.'],
      },
      {
        h2: 'External services',
        paragraphs: [
          'Demo booking opens Calendly on its own site, and the social media links lead to those services. They have their own cookie policies. See the privacy policy for more on data processing.',
        ],
      },
    ],
  },
  // TARKISTETTAVA: German translation of the Finnish cookie policy — have it reviewed.
  de: {
    title: 'Cookie-Richtlinie',
    updatedLabel: 'Stand',
    updated: UPDATED,
    sections: [
      {
        h2: 'Diese Website verwendet keine Cookies',
        paragraphs: [
          'Diese Website setzt keine Cookies und verwendet keine Analyse-, Werbe- oder Tracking-Werkzeuge. Sie lädt auch keine Inhalte Dritter wie eingebettete Videos, Karten oder externe Schriftarten. Deshalb gibt es kein Cookie-Banner.',
        ],
      },
      {
        h2: 'Lokale Speicherung im Browser',
        paragraphs: ['Die Website speichert nur, was die von Ihnen gewünschte Funktion erfordert:'],
        list: [
          'Sprachwahl: damit die Website in der gewählten Sprache erscheint (bleibt gespeichert, bis Sie die Browserdaten löschen).',
          'Geschlossene Hinweise: damit ein geschlossener Hinweis nicht erneut erscheint.',
          'Nicht abgeschlossenes Formular: damit das Formular zur Einsparungsschätzung Ihre Angaben während des Ausfüllens behält (wird beim Schließen des Tabs gelöscht).',
        ],
        after: ['Diese Daten verbleiben in Ihrem Browser und werden nicht zum Tracking verwendet.'],
      },
      {
        h2: 'Externe Dienste',
        paragraphs: [
          'Die Terminbuchung für eine Demo öffnet Calendly auf dessen eigener Website, und die Social-Media-Links führen zu den jeweiligen Diensten. Diese haben eigene Cookie-Richtlinien. Weitere Informationen finden Sie in der Datenschutzerklärung.',
        ],
      },
    ],
  },
};
